/**
 * AuoReviewPanel — the staff AUO confirm/reject SIDE LIST (Slice 5 redesign
 * 2026-06-08). Lives in the FIXED side region of the floor workspace: every
 * marked AUO is a numbered CARD, stacked one below another, so the staff can
 * dispose of all of them from one place without scrolling the map up and down.
 *
 * Each card = its NUMBER (ties to the on-map marker) · name (one auto-search
 * across all 3 categories) · the covered ZONE(S) · the engine disposition +
 * reason PER covered zone (an AUO shape straddling two zones gets two lines) ·
 * shape controls (square/rectangle/circle + rotate) · Confirm / Reject / Remove.
 * Selecting a card highlights its marker (and vice-versa) — two-way reference by
 * number.
 *
 * Pure presentational: all mutations route through the parent's pure auoMarks
 * ops. The zones are always DERIVED from the shape; an "Edit zones" override
 * exists for the rare edge case.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Btn3, Tag } from './ui';
import {
  AUO_TYPES,
  reviewCounts,
  pendingReviewCount,
  regionForMark,
  type AuoMark,
} from '../lib/auoMarks';
import { ZONE_CODES } from '../lib/staffIntake';
import {
  type AuoPalette,
  type AuoPaletteItem,
} from '../lib/auoPalette';
import AuoSearchBox from './AuoSearchBox';
import DispositionEditor from './DispositionEditor';
import { type OverrideDisposition } from '../lib/auoOverrides';
// Shared disposition semantics (Task 7) — ONE source of truth for the ruling
// word + severity colour + "no ruling" test, so this inline panel and the
// canonical workspace list (EvalDispositionList) never diverge again.
import {
  dispositionColor as dispColour,
  dispositionLabel,
  isNoRuling,
  swatchCss,
} from '../lib/auoDisposition';

// Pure-white field on a pure-white panel: distinction via border + DARK ink,
// never a tinted box (feedback_pure_white_html_backgrounds). The literal
// `#1a1a1a` (== --ink) is a hard fallback so the typed text can NEVER inherit a
// light colour and go white-on-white, even if a var fails to resolve here.
const cell: React.CSSProperties = {
  border: '1px solid var(--rule-strong)',
  borderRadius: 'var(--r-sm)',
  padding: '6px 8px',
  fontSize: 12.5,
  fontFamily: 'inherit',
  color: 'var(--ink, #1a1a1a)',
  background: '#FFFFFF',
};

// swatchCss (corrective-colour name → CSS) now lives in ../lib/auoDisposition
// (shared with the workspace list EvalDispositionList) — imported at the top.

const statusColour = (s: AuoMark['status']) =>
  s === 'confirmed' ? '#0d9488' : s === 'rejected' ? '#9ca3af' : '#d97706';

export default function AuoReviewPanel({
  marks,
  palette,
  busy,
  canSuggest,
  selectedId = null,
  onSelect,
  onSuggest,
  onSetName,
  onSetType,
  onSetNameType,
  onSetZones,
  onSetShape,
  onRotate,
  onConfirm,
  onReject,
  onReopen,
  onRemove,
  isAdmin = false,
  onCorrectDisposition,
  onInlineAdd,
}: {
  marks: AuoMark[];
  palette: AuoPalette | null;
  busy: boolean;
  /** the floor has a grid (≥3-pt boundary) — only then can AUOs be placed/asked. */
  canSuggest: boolean;
  /** the AUO selected on the map / in this list (two-way reference by number). */
  selectedId?: string | null;
  onSelect: (id: string | null) => void;
  onSuggest: () => void;
  onSetName: (id: string, name: string) => void;
  onSetType: (id: string, type: string) => void;
  /** a palette pick → set name + derived type in ONE patch (no clobber). */
  onSetNameType: (id: string, name: string, type: string) => void;
  /** override the derived zone set (rare edge case). */
  onSetZones: (id: string, zones: string[]) => void;
  /** switch the AUO shape (square/rectangle = rect; circle). */
  onSetShape: (id: string, kind: 'rect' | 'circle') => void;
  /** rotate a rect AUO (deg). */
  onRotate: (id: string, deg: number) => void;
  onConfirm: (id: string) => void;
  onReject: (id: string) => void;
  /** re-open a confirmed/rejected AUO for editing (status → proposed, kept
   *  suggestions). The "Edit" affordance on a confirmed card. */
  onReopen: (id: string) => void;
  onRemove: (id: string) => void;
  /** admin/owner → may CORRECT a per-zone disposition (the override loop). */
  isAdmin?: boolean;
  /** persist an owner correction for one (AUO × zone): disposition/effect/remedy.
   *  Admin-gated; the parent writes it to `vastu_auo_overrides` + reflects it. */
  onCorrectDisposition?: (
    id: string,
    zone: string,
    patch: {
      disposition: OverrideDisposition;
      effect: string;
      management?: string | null;
      priorSource?: string | null;
    },
  ) => void;
  /** a custom name not in the palette → record it as a pending palette item. */
  onInlineAdd: (name: string) => void;
}) {
  const counts = useMemo(() => reviewCounts(marks), [marks]);
  const pending = useMemo(() => pendingReviewCount(marks), [marks]);
  // the full flattened palette (all 3 categories) for the unified auto-search.
  const paletteItems = useMemo<AuoPaletteItem[]>(
    () => (palette ? palette.items : []),
    [palette],
  );
  // lowercase label set → tells a free-typed value from a real palette pick
  // (a free value = inline-add candidate).
  const paletteLabelSet = useMemo(
    () => new Set(paletteItems.map((it) => it.label.toLowerCase())),
    [paletteItems],
  );

  return (
    <div
      style={{
        // FIXED side region: a scroll container for the stacked numbered cards.
        // The header (count + Ask engine) stays put; only the card list scrolls.
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        height: '100%',
      }}
      data-testid="auo-review-panel"
    >
      <div
        style={{
          display: 'flex',
          gap: 10,
          alignItems: 'center',
          flexWrap: 'wrap',
          marginBottom: 8,
          paddingBottom: 8,
          borderBottom: '1px solid var(--rule)',
          flex: '0 0 auto',
        }}
      >
        <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--ink)' }}>
          AUO review ({counts.total})
        </span>
        {counts.confirmed > 0 && (
          <Tag kind="val">{counts.confirmed} confirmed</Tag>
        )}
        {pending > 0 && (
          <Tag kind="warn" title="Proposed fixtures still need a Confirm/Reject — un-actioned ones are NOT in the report">
            {pending} pending review
          </Tag>
        )}
        {counts.rejected > 0 && (
          <Tag kind="default">{counts.rejected} rejected</Tag>
        )}
        {counts.unrecognised > 0 && (
          <Tag kind="warn" title="The engine cannot place these names — rename or remove">
            {counts.unrecognised} unrecognised
          </Tag>
        )}
        <span style={{ marginLeft: 'auto' }}>
          <Btn3
            intent="ink"
            size="sm"
            onClick={onSuggest}
            disabled={busy || !canSuggest || marks.length === 0}
            title={
              !canSuggest
                ? 'Trace the boundary first — the grid must exist'
                : 'Ask the engine for each AUO’s disposition (Favourable / Remedy Required / Not Permissible) per covered zone'
            }
          >
            {busy ? 'Asking engine…' : 'Ask engine for dispositions'}
          </Btn3>
        </span>
      </div>

      {marks.length === 0 ? (
        <p style={{ fontSize: 11.5, color: 'var(--ink-mute)', margin: '0 0 4px' }}>
          Switch to “Mark AUO”, press “+ Add AUO”, and click inside the plot to
          drop a fixture (Toilet, Kitchen, Bed …). It is placed as a SHAPE — resize
          it to cover the real footprint, and every zone it overlaps is read off
          the grid. Then name it, ask the engine, and Confirm the ones that belong.
        </p>
      ) : (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            overflowY: 'auto',
            flex: '1 1 auto',
            minHeight: 0,
            paddingRight: 2,
          }}
        >
          {marks.map((m, idx) => (
            <AuoCardBody
              key={m.id}
              num={idx + 1}
              mark={m}
              selected={selectedId === m.id}
              paletteItems={paletteItems}
              isInPalette={(name) => paletteLabelSet.has(name.trim().toLowerCase())}
              onSelect={onSelect}
              onSetName={onSetName}
              onSetType={onSetType}
              onSetNameType={onSetNameType}
              onSetZones={onSetZones}
              onSetShape={onSetShape}
              onRotate={onRotate}
              onConfirm={onConfirm}
              onReject={onReject}
              onReopen={onReopen}
              onRemove={onRemove}
              isAdmin={isAdmin}
              onCorrectDisposition={onCorrectDisposition}
              onInlineAdd={onInlineAdd}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * AuoCardBody — the EXACT rich per-AUO review card (the "beautifully made thing":
 * number badge · name search · shape ▭/◯ + rotate · type · zones + ✎ edit · the
 * engine's per-zone ruling with corrective swatch + ✓ owner-corrected + admin ✎
 * correct-ruling · Confirm / Reject / Reopen / Remove). Exported UNCHANGED so BOTH
 * surfaces render the SAME component byte-identically (shared-language "reuse"):
 *   · the inline staff panel (AuoReviewPanel, above) — num = its 1..N list index;
 *   · the workspace unified list (UnifiedDispoList) — num = the row's UNIFIED
 *     seqNo (matches the map pins), NOT this panel's internal index.
 * NOT a redraw — one JSX, two mounts.
 */
export function AuoCardBody({
  num,
  mark: m,
  selected,
  paletteItems,
  isInPalette,
  onSelect,
  onSetName,
  onSetType,
  onSetNameType,
  onSetZones,
  onSetShape,
  onRotate,
  onConfirm,
  onReject,
  onReopen,
  onRemove,
  isAdmin,
  onCorrectDisposition,
  onInlineAdd,
}: {
  num: number;
  mark: AuoMark;
  selected: boolean;
  paletteItems: AuoPaletteItem[];
  isInPalette: (name: string) => boolean;
  onSelect: (id: string | null) => void;
  onSetName: (id: string, name: string) => void;
  onSetType: (id: string, type: string) => void;
  onSetNameType: (id: string, name: string, type: string) => void;
  onSetZones: (id: string, zones: string[]) => void;
  onSetShape: (id: string, kind: 'rect' | 'circle') => void;
  onRotate: (id: string, deg: number) => void;
  onConfirm: (id: string) => void;
  onReject: (id: string) => void;
  onReopen: (id: string) => void;
  onRemove: (id: string) => void;
  isAdmin: boolean;
  onCorrectDisposition?: (
    id: string,
    zone: string,
    patch: {
      disposition: OverrideDisposition;
      effect: string;
      management?: string | null;
      priorSource?: string | null;
    },
  ) => void;
  onInlineAdd: (name: string) => void;
}) {
  const [editZones, setEditZones] = useState(false);
  // which per-zone ruling is open for owner-correction (zone code, or null).
  const [editingZone, setEditingZone] = useState<string | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const name = (m.name || '').trim();
  const customName = !!name && !isInPalette(name);
  const zones = m.zones || [];
  const region = regionForMark(m);
  const sugs = m.suggestionsByZone || {};
  const unrecognised = Object.values(sugs).some((s) => s.recognised === false);
  const statusBg =
    m.status === 'confirmed'
      ? 'var(--inset, #f3faf8)'
      : m.status === 'rejected'
        ? '#f6f6f6'
        : '#fff';

  // scroll a freshly-SELECTED card into view (when selected from the map).
  useEffect(() => {
    if (selected && cardRef.current) {
      cardRef.current.scrollIntoView({ block: 'nearest' });
    }
  }, [selected]);

  return (
    <div
      ref={cardRef}
      data-auo-card={m.id}
      onMouseDown={() => onSelect(m.id)}
      style={{
        border: selected ? `2px solid ${statusColour(m.status)}` : '1px solid var(--rule)',
        borderLeft: `4px solid ${statusColour(m.status)}`,
        borderRadius: 'var(--r-sm)',
        background: statusBg,
        padding: '8px 10px',
        cursor: 'pointer',
      }}
    >
      {/* row 0 — the NUMBER badge + name search + remove */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
        {/* the numbered badge — matches the on-map marker */}
        <span
          aria-label={`AUO ${num}`}
          style={{
            flex: '0 0 auto',
            width: 24,
            height: 24,
            borderRadius: 999,
            background: statusColour(m.status),
            color: '#fff',
            fontSize: 12.5,
            fontWeight: 800,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginTop: 2,
          }}
        >
          {num}
        </span>
        <div style={{ flex: '1 1 auto' }}>
          <AuoSearchBox
            value={m.name}
            type={m.type}
            paletteItems={paletteItems}
            onPick={(item) => onSetNameType(m.id, item.label, item.type)}
            onTypeText={(text) => onSetName(m.id, text)}
            onCommitCustom={(text) => {
              const v = text.trim();
              if (v && !isInPalette(v)) onInlineAdd(v);
            }}
          />
        </div>
      </div>

      {/* row 1 — shape controls + type + covered zones */}
      <div
        style={{
          display: 'flex',
          gap: 8,
          alignItems: 'center',
          flexWrap: 'wrap',
          marginTop: 8,
        }}
      >
        {/* shape kind toggle (square/rectangle = rect; circle) */}
        <div role="group" aria-label="AUO shape" style={{ display: 'inline-flex', border: '1px solid var(--rule-strong)', borderRadius: 'var(--r-sm)', overflow: 'hidden' }}>
          {(['rect', 'circle'] as const).map((k, ki) => {
            const on = region.kind === k;
            return (
              <button
                key={k}
                type="button"
                aria-label={k === 'rect' ? 'Rectangle' : 'Circle'}
                onClick={(e) => {
                  e.stopPropagation();
                  onSetShape(m.id, k);
                }}
                style={{
                  border: 'none',
                  borderRight: ki === 0 ? '1px solid var(--rule-strong)' : 'none',
                  padding: '5px 9px',
                  fontSize: 13,
                  fontFamily: 'inherit',
                  cursor: 'pointer',
                  background: on ? 'var(--ink)' : '#fff',
                  color: on ? '#fff' : 'var(--ink)',
                }}
              >
                {k === 'rect' ? '▭' : '◯'}
              </button>
            );
          })}
        </div>

        {/* rotate (rect only) */}
        {region.kind === 'rect' && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onRotate(m.id, (region.rotation + 15) % 360);
            }}
            title="Rotate 15°"
            aria-label="Rotate AUO"
            style={{ ...cell, cursor: 'pointer' }}
          >
            ↻ {Math.round(region.rotation)}°
          </button>
        )}

        {/* type — auto-derived from the picked AUO; editable fallback */}
        <select
          style={{ ...cell }}
          value={m.type}
          onChange={(e) => onSetType(m.id, e.target.value)}
          onClick={(e) => e.stopPropagation()}
          aria-label="AUO type"
          title="Category — set automatically when you pick an AUO; change it only for a custom name"
        >
          {AUO_TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </div>

      {/* row 2 — covered zones (DERIVED set) with an Edit override */}
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginTop: 8 }}>
        <span style={{ fontSize: 11, color: 'var(--ink-mute)', fontWeight: 600 }}>
          zones:
        </span>
        {zones.length === 0 ? (
          <Tag kind="warn">off-grid</Tag>
        ) : (
          zones.map((z) => (
            <span
              key={z}
              aria-label={`zone ${z}`}
              style={{
                fontSize: 11.5,
                fontWeight: 700,
                color: 'var(--ink, #1a1a1a)',
                border: '1px solid var(--rule-strong)',
                borderRadius: 999,
                padding: '1px 8px',
                background: '#fff',
              }}
            >
              {z}
            </span>
          ))
        )}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setEditZones((v) => !v);
          }}
          title="Zones are read off the grid — click to override"
          style={{
            border: 'none',
            background: 'transparent',
            color: 'var(--ink-mute)',
            fontSize: 12,
            fontFamily: 'inherit',
            cursor: 'pointer',
            textDecoration: 'underline',
          }}
          aria-label="Edit zones"
        >
          ✎ edit
        </button>
      </div>

      {editZones && (
        <div style={{ marginTop: 6, display: 'flex', gap: 4, flexWrap: 'wrap' }} onClick={(e) => e.stopPropagation()}>
          {ZONE_CODES.map((z) => {
            const on = zones.includes(z);
            return (
              <button
                key={z}
                type="button"
                aria-pressed={on}
                onClick={() =>
                  onSetZones(
                    m.id,
                    on ? zones.filter((x) => x !== z) : [...zones, z],
                  )
                }
                style={{
                  border: '1px solid var(--rule-strong)',
                  borderRadius: 'var(--r-sm)',
                  padding: '3px 7px',
                  fontSize: 11,
                  fontFamily: 'inherit',
                  cursor: 'pointer',
                  background: on ? 'var(--ink)' : '#fff',
                  color: on ? '#fff' : 'var(--ink)',
                }}
              >
                {z}
              </button>
            );
          })}
        </div>
      )}

      {/* row 3 — the engine's REAL per-zone ruling from the owner's Vastu XLS:
          disposition (colour-coded by severity) · the owner's VERBATIM effect
          text · the remedy (management) if any · the corrective-colour swatch.
          ONE block per covered zone (a shape straddling N zones → N blocks).
          A db-miss zone shows a neutral "No ruling yet" state — this is where a
          future inline-dictation loop hooks (see TODO below). */}
      {Object.keys(sugs).length > 0 && (
        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {zones
            .filter((z) => sugs[z])
            .map((z) => {
              const s = sugs[z];
              const noRuling = isNoRuling(s);
              return (
                <div
                  key={z}
                  data-zone-ruling={z}
                  data-no-ruling={noRuling ? '1' : '0'}
                  style={{
                    borderLeft: `3px solid ${dispColour(s.disposition)}`,
                    paddingLeft: 8,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                  }}
                >
                  {/* zone + disposition badge + corrective-colour swatch */}
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-mute)', minWidth: 36 }}>
                      {z}
                    </span>
                    <span style={{ fontSize: 11.5, fontWeight: 800, color: dispColour(s.disposition) }}>
                      {dispositionLabel(s.disposition, s.recognised !== false)}
                    </span>
                    {!noRuling && s.corrective_colour && (
                      <span
                        title={`Corrective colour: ${s.corrective_colour}${s.corrective_metal ? ` · metal: ${s.corrective_metal}` : ''}`}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginLeft: 'auto' }}
                      >
                        <span
                          aria-hidden
                          style={{
                            width: 12,
                            height: 12,
                            borderRadius: 3,
                            border: '1px solid var(--rule, #e5e7eb)',
                            background: swatchCss(s.corrective_colour),
                            display: 'inline-block',
                          }}
                        />
                        <span style={{ fontSize: 10.5, color: 'var(--ink-mute)' }}>{s.corrective_colour}</span>
                      </span>
                    )}
                  </div>
                  {noRuling ? (
                    /* db-miss → neutral, not alarming. The owner inline-dictation
                       loop hooks HERE: an admin "Add ruling" opens the SAME
                       correction form a wrong-derived cell uses (Issue 2). */
                    <span style={{ fontSize: 11.5, color: 'var(--ink-mute)', fontStyle: 'italic' }}>
                      No ruling yet for this placement — flag for the owner.
                    </span>
                  ) : (
                    <>
                      {/* the owner's VERBATIM effect text (ground truth) */}
                      {s.effect && (
                        <span style={{ fontSize: 11.5, color: 'var(--ink, #1a1a1a)' }}>{s.effect}</span>
                      )}
                      {/* the remedy, if the XLS authored one (or owner-corrected) */}
                      {s.management && (
                        <span style={{ fontSize: 11, color: '#b45309' }}>
                          <strong>Remedy:</strong> {s.management}
                        </span>
                      )}
                      {s.source === 'override' && (
                        <span style={{ fontSize: 10, color: '#0d9488', fontWeight: 700 }}>
                          ✓ owner-corrected
                        </span>
                      )}
                    </>
                  )}

                  {/* ⭐ OVERRIDE LOOP (admin/owner only) — correct a WRONG-derived
                      cell OR fill a db-miss "No ruling yet" cell, in ONE flow. The
                      correction persists to vastu_auo_overrides and is used
                      everywhere thereafter. Needs the canonical key (s.normalised).
                      TODO(override-reconciliation): these overrides are folded back
                      into auo_dispositions_canonical.json + the owner's XLS by a
                      future reconciliation step (see auoOverrides.ts). */}
                  {isAdmin && onCorrectDisposition && s.normalised && (
                    editingZone === z ? (
                      <DispositionEditor
                        zone={z}
                        current={s}
                        onCancel={() => setEditingZone(null)}
                        onSave={(patch) => {
                          onCorrectDisposition(m.id, z, {
                            ...patch,
                            priorSource: s.source ?? null,
                          });
                          setEditingZone(null);
                        }}
                      />
                    ) : (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingZone(z);
                        }}
                        style={{
                          alignSelf: 'flex-start',
                          marginTop: 2,
                          border: 'none',
                          background: 'transparent',
                          color: 'var(--ink-mute)',
                          fontSize: 11,
                          fontFamily: 'inherit',
                          cursor: 'pointer',
                          textDecoration: 'underline',
                        }}
                        aria-label={`Correct ruling for ${z}`}
                      >
                        {noRuling ? '✎ add ruling' : '✎ correct ruling'}
                      </button>
                    )
                  )}
                </div>
              );
            })}
        </div>
      )}

      {/* warnings */}
      {unrecognised && (
        <p style={{ fontSize: 11.5, color: 'var(--crit, #dc2626)', margin: '6px 0 0' }}>
          “{name}” isn’t a recognised AUO — the engine will skip it. Rename it to a
          palette item, or remove it.
        </p>
      )}
      {customName && Object.keys(sugs).length === 0 && (
        <p style={{ fontSize: 11, color: 'var(--ink-mute)', margin: '6px 0 0' }}>
          Custom name — saved for admin review. Ask the engine to check it
          normalises before confirming.
        </p>
      )}

      {/* row 4 — decisions */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 8 }}>
        {m.status === 'confirmed' ? (
          <Tag kind="val">confirmed → in report</Tag>
        ) : m.status === 'rejected' ? (
          <Tag kind="default">rejected{m.rejected_by ? ` · ${m.rejected_by}` : ''}</Tag>
        ) : (
          <Tag kind="warn">proposed</Tag>
        )}
        <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 8 }}>
          {/* EDIT — a confirmed/rejected AUO is otherwise locked; this re-opens it
              (status → proposed) so its size/position/zone/name/disposition can be
              changed, then re-confirmed. Keeps the existing rulings visible. */}
          {m.status !== 'proposed' && (
            <Btn3
              intent="ghost"
              size="sm"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                onReopen(m.id);
                onSelect(m.id); // select so its on-map grips appear for resize/move
              }}
              title="Edit this AUO — re-open it to change size, position, zone, name, or correct its ruling"
              aria-label="Edit AUO"
            >
              Edit
            </Btn3>
          )}
          {m.status !== 'confirmed' && (
            <Btn3
              intent="grn"
              size="sm"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                onConfirm(m.id);
              }}
              disabled={zones.length === 0 || !name}
              title={zones.length === 0 || !name ? 'Name the AUO + place it on the grid first' : 'Confirm — include in the report'}
            >
              Confirm
            </Btn3>
          )}
          {m.status !== 'rejected' && (
            <Btn3 intent="ghost" size="sm" onClick={(e: React.MouseEvent) => { e.stopPropagation(); onReject(m.id); }}>
              Reject
            </Btn3>
          )}
          <Btn3 intent="ghost" size="sm" onClick={(e: React.MouseEvent) => { e.stopPropagation(); onRemove(m.id); }} aria-label="Remove AUO">
            Remove
          </Btn3>
        </span>
      </div>
    </div>
  );
}

/* DispositionEditor now lives in ./DispositionEditor (shared with the
 * eval-workspace right-pane naming card) — imported at the top of this file.
 * AuoSearchBox now lives in ./AuoSearchBox (shared with the eval-workspace
 * right-pane naming card) — imported at the top of this file. */
